import { useState, useRef, useEffect } from 'react'
import { useUpdateGoal } from '../mutations/useUpdateGoal.js'
import { colorFor } from '../data/userColors.js'

const TEAM = [
  'Ricky', 'Zoe', 'Christina', 'Katie', 'Margo', 'Emily',
  'Sandra', 'Sergina', 'Jonathan', 'Chris', 'Harold',
  'Jessie', 'Christine', 'Braden', 'Colin', 'Jane',
]

/**
 * SupportPicker — multi-select chip picker for the "support" slot on a
 * goal (the people helping the DRI, stored as a comma-separated string
 * in goal.owner).
 *
 * Data model:
 *   goal.owner = "Zoe, Katie"  (comma-separated, sorted, excludes DRI)
 *
 * Behavior:
 *   • Each supporter renders as a colored chip with an × to remove.
 *   • A trailing "＋" button opens a dropdown of team members that
 *     haven't been added yet (and aren't the DRI).
 *   • Each add/remove writes the new comma-separated string via
 *     useUpdateGoal, so changes are optimistic and undoable.
 *
 * Props:
 *   pillarName   — which pillar the goal lives in
 *   goalIndex    — index of the goal
 *   dri          — the DRI name (filtered out of the picker)
 *   ownerField   — current value of goal.owner (may be empty)
 *   label        — label prefix shown before chips ("Support" | "Owner")
 */
export default function SupportPicker({
  pillarName,
  goalIndex,
  dri,
  ownerField,
  label = 'Support',
}) {
  const mutation = useUpdateGoal()

  // Parse the comma-separated string into a clean array, filtered
  const supporters = parseSupporters(ownerField, dri)

  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e) => {
      if (wrapRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const t = setTimeout(() => {
      document.addEventListener('click', onClick)
      document.addEventListener('keydown', onKey)
    }, 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('click', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const writeSupporters = (next) => {
    const value = next.join(', ')
    mutation.mutate({ pillarName, goalIndex, field: 'owner', value })
  }

  const addSupporter = (name) => {
    if (supporters.includes(name) || name === dri) return
    writeSupporters([...supporters, name])
    setOpen(false)
  }

  const removeSupporter = (name) => {
    writeSupporters(supporters.filter((s) => s !== name))
  }

  const available = TEAM.filter((n) => n !== dri && !supporters.includes(n))

  if (supporters.length === 0 && !open) {
    // When nobody's helping, render a subtle + button so it's still easy
    // to add supporters later.
    return (
      <div ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setOpen(true)
          }}
          title={`Add ${label.toLowerCase()}`}
          style={{
            ...addBtnStyle,
            color: 'var(--muted)',
            borderStyle: 'dashed',
          }}
        >
          ＋ {label}
        </button>
        {open && (
          <Dropdown
            options={available}
            onPick={addSupporter}
            onClose={() => setOpen(false)}
          />
        )}
      </div>
    )
  }

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--muted)',
          marginRight: 1,
        }}
      >
        {label}:
      </span>
      {supporters.map((name) => (
        <SupporterChip key={name} name={name} onRemove={() => removeSupporter(name)} />
      ))}
      {available.length > 0 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setOpen((x) => !x)
            }}
            title={`Add another ${label.toLowerCase()}`}
            style={addBtnStyle}
          >
            ＋
          </button>
          {open && (
            <Dropdown
              options={available}
              onPick={addSupporter}
              onClose={() => setOpen(false)}
            />
          )}
        </>
      )}
    </div>
  )
}

/* ─── Pieces ──────────────────────────────────────────────────────── */

function SupporterChip({ name, onRemove }) {
  const color = colorFor(name)
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        background: color + '18',
        color,
        border: `1px solid ${color}30`,
        borderRadius: 10,
        padding: '1px 2px 1px 8px',
        fontSize: 10,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {name}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
        title={`Remove ${name}`}
        aria-label={`Remove ${name}`}
        style={{
          width: 14,
          height: 14,
          border: 'none',
          background: 'transparent',
          color,
          fontSize: 11,
          cursor: 'pointer',
          padding: 0,
          lineHeight: 1,
          opacity: 0.6,
          borderRadius: 99,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
      >
        ×
      </button>
    </span>
  )
}

function Dropdown({ options, onPick, onClose }) {
  if (options.length === 0) {
    return (
      <div style={dropdownStyle}>
        <div style={{ ...itemStyle, color: 'var(--muted-light)', fontStyle: 'italic' }}>
          Everyone's already here
        </div>
      </div>
    )
  }
  return (
    <div style={dropdownStyle}>
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          padding: '4px 8px 6px',
        }}
      >
        Add supporter
      </div>
      {options.map((name) => {
        const c = colorFor(name)
        return (
          <button
            key={name}
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onPick(name)
            }}
            style={itemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <span
              style={{
                display: 'inline-block',
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: c,
                color: '#fff',
                fontSize: 10,
                fontWeight: 800,
                lineHeight: '18px',
                textAlign: 'center',
              }}
            >
              {name[0]}
            </span>
            {name}
          </button>
        )
      })}
    </div>
  )
}

/* ─── Helpers + shared styles ─────────────────────────────────────── */

function parseSupporters(ownerField, dri) {
  return String(ownerField || '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s && s !== '—' && s !== dri)
}

const addBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  background: '#fff',
  color: 'var(--muted)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '1px 8px',
  fontSize: 10,
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  userSelect: 'none',
}

const dropdownStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  minWidth: 160,
  maxHeight: 280,
  overflowY: 'auto',
  background: '#fff',
  border: '1px solid var(--border)',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
  padding: 6,
  zIndex: 950,
}

const itemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  border: 'none',
  background: 'transparent',
  padding: '6px 8px',
  borderRadius: 6,
  fontSize: 12,
  color: 'var(--text)',
  cursor: 'pointer',
  textAlign: 'left',
}
